import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Brand from "../components/Brand";
import { MapPin, Sparkles, ShieldCheck, Sun } from "lucide-react";

const BG = "https://images.unsplash.com/photo-1679486689377-e4fcf6861a67?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHx0b3BvZ3JhcGh5JTIwbWFwJTIwdGV4dHVyZXxlbnwwfHx8fDE3ODE0MTczODN8MA&ixlib=rb-4.1.0&q=85";

const DOMAINS = [
  { icon: MapPin, title: "Ecological Intelligence", items: ["Site characteristics", "Species associations", "Environmental conditions"] },
  { icon: Sparkles, title: "Genetic Intelligence", items: ["Provenance", "Population diversity", "Germplasm information"] },
  { icon: ShieldCheck, title: "Cultural Intelligence", items: ["Traditional Owner authority", "Cultural permissions", "Knowledge governance"] },
  { icon: Sun, title: "Climate Intelligence", items: ["Environmental change indicators", "Climate projections", "Adaptation opportunities"] },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("admin@seedkeepers.org");
  const [password, setPassword] = useState("SeedKeeper2026!");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      const to = location.state?.from?.pathname || "/dashboard";
      navigate(to, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.2fr_1fr] bg-background" data-testid="login-page">
      <div className="hidden lg:flex relative overflow-hidden">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2C2A28]/90 via-[#5C5854]/75 to-[#8B5A2B]/65" />
        <div className="relative z-10 p-12 xl:p-14 flex flex-col text-[#FDFBF7] overflow-y-auto max-h-screen w-full">
          <Brand variant="inline" tone="light" size={44} testid="login-brand" />

          <div className="mt-12">
            <div className="font-display text-[64px] xl:text-[80px] leading-none tracking-[0.18em] font-bold" data-testid="login-spirit">SPIRIT</div>
            <h1 className="font-serif-display text-2xl xl:text-3xl leading-[1.15] font-medium tracking-tight mt-5 max-w-xl">
              Seed Provenance Intelligence and Restoration Information Technology
            </h1>
            <p className="mt-6 text-[13.5px] xl:text-[14px] leading-relaxed text-[#FDFBF7]/90 max-w-xl">
              An integrated provenance intelligence platform for the collection, management and analysis of
              provenance, population genetics, ecological metadata, cultural governance and climate adaptation
              datasets associated with native seed resources.
            </p>
            <p className="mt-3 text-[13px] leading-relaxed text-[#FDFBF7]/80 italic font-serif-display max-w-xl">
              Supporting evidence-based restoration, conservation and environmental stewardship across Indigenous
              and non-Indigenous landscapes.
            </p>
          </div>

          <div className="mt-10">
            <div className="text-[10px] tracking-[0.24em] uppercase font-semibold text-[#FDFBF7]/90">Four Integrated Knowledge Domains</div>
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              {DOMAINS.map((d) => (
                <div key={d.title} className="rounded-md border border-white/15 bg-white/[0.06] backdrop-blur-sm p-4">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-md bg-white/10 border border-white/20 flex items-center justify-center">
                      <d.icon className="w-3.5 h-3.5" strokeWidth={1.8} />
                    </div>
                    <div className="font-display font-semibold text-[13px] tracking-tight">{d.title}</div>
                  </div>
                  <ul className="mt-2.5 space-y-1 text-[11.5px] leading-snug text-[#FDFBF7]/85">
                    {d.items.map((it) => (
                      <li key={it} className="flex items-start gap-1.5">
                        <span className="mt-[6px] w-1 h-1 rounded-full bg-white/60 shrink-0" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <p className="mt-5 text-[12.5px] leading-relaxed text-[#FDFBF7]/90 italic font-serif-display max-w-2xl border-l-2 border-white/30 pl-4">
              SPIRIT integrates these knowledge domains into a single provenance intelligence framework for native
              seed systems.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16 sk-contour">
        <form onSubmit={submit} className="w-full max-w-md sk-fade-in" data-testid="login-form">
          <div className="lg:hidden mb-8">
            <Brand variant="inline" tone="default" size={44} />
          </div>

          <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#8B5A2B] mb-3">Sign in</div>
          <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight text-[#2C2A28]">Welcome back.</h2>
          <p className="text-[13.5px] text-[#5C5854] mt-2">Continue your seed provenance work.</p>

          <div className="space-y-5 mt-8">
            <div>
              <Label htmlFor="email" className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#5C5854]">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 bg-white border-[#E6E2D8] focus-visible:ring-[#8B5A2B]" data-testid="login-email" />
            </div>
            <div>
              <Label htmlFor="password" className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#5C5854]">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 bg-white border-[#E6E2D8] focus-visible:ring-[#8B5A2B]" data-testid="login-password" />
            </div>
            {error && <div className="text-[12px] text-[#B45B3E]" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={loading} className="w-full bg-[#8B5A2B] hover:bg-[#6B4420] text-[#FDFBF7]" data-testid="login-submit">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </div>
          <p className="mt-8 text-[13px] text-[#5C5854]">
            New to SeedKeepers? <Link to="/register" className="text-[#8B5A2B] font-semibold underline-offset-4 hover:underline" data-testid="link-register">Create an account</Link>
          </p>
          <p className="mt-3 text-[12px] text-[#8A857D]">
            Looking for public data? <Link to="/atlas" className="text-[#8B5A2B] font-semibold underline-offset-4 hover:underline" data-testid="link-atlas">Open the Restoration Atlas →</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
